import { useEffect, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { getAuthWebsiteUrl, getAuthShowLinkedIn, AUTH_LINKEDIN_URL } from "../../config/auth-ui";
import {
  getWhiteLabelKey,
  getWhiteLabelLogoHeightScale,
  usesCustomLeftVisual,
} from "../../config/white-label";
import { useTheme } from "../../context/ThemeContext";
import { getLoginHeroFallbackUrl } from "./authHeroUrls";

function LinkedInIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

export default function AuthLeftVisual({ heroUrl, stretch = false }) {
  const { isDarkMode } = useTheme();
  const isCustomLeftVisual = usesCustomLeftVisual();
  const whiteLabelKey = getWhiteLabelKey();
  const heroAlt =
    whiteLabelKey === "ipasoft"
      ? "IPA Soft"
      : whiteLabelKey === "jimssoft"
        ? "Jim's Tax"
        : "Exacc — business management software";
  const showLinkedIn = getAuthShowLinkedIn();
  const websiteUrl = getAuthWebsiteUrl();
  const logoHeightScale = getWhiteLabelLogoHeightScale();
  const [imgSrc, setImgSrc] = useState(heroUrl);

  useEffect(() => {
    setImgSrc(heroUrl);
  }, [heroUrl]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 2.5,
        width: "100%",
        maxWidth: 460,
        ml: "auto",
        position: "relative",
        zIndex: 2,
        ...(stretch && !isCustomLeftVisual
          ? { flex: 1, minHeight: 0, height: "100%" }
          : {}),
      }}
    >
      <Box
        className="register-visual"
        sx={{
          width: "100%",
          ...(isCustomLeftVisual
            ? {}
            : stretch
              ? {
                  flex: 1,
                  minHeight: { md: 280, lg: 320 },
                }
              : {}),
        }}
      >
        <img
          src={imgSrc}
          alt={heroAlt}
          className="register-visual__img"
          loading="eager"
          style={
            logoHeightScale !== 1
              ? {
                  transform: `scale(${logoHeightScale})`,
                  transformOrigin: "center center",
                }
              : undefined
          }
          onError={() => {
            const fallbackUrl = getLoginHeroFallbackUrl(isDarkMode);
            if (imgSrc !== fallbackUrl) {
              setImgSrc(fallbackUrl);
            }
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", px: 0.5 }}>
        <Link
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            color: "primary.main",
            fontWeight: 600,
            fontSize: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          Visit Our Website
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </Link>

        {showLinkedIn && (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              or
            </Typography>

            <Link
              href={AUTH_LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Exacc on LinkedIn"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: "rgba(10, 102, 194, 0.1)",
                border: "1px solid rgba(10, 102, 194, 0.25)",
                color: "#4a9fd5",
                transition: "all 0.2s ease",
                "&:hover": {
                  background: "rgba(10, 102, 194, 0.18)",
                  borderColor: "rgba(10, 102, 194, 0.45)",
                  color: "#6ab4e8",
                },
              }}
            >
              <LinkedInIcon />
            </Link>
          </>
        )}
      </Box>
    </Box>
  );
}
